import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { newsAPI } from "../services/api";

const NewsDetailPage = () => {
  const { id } = useParams();

  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  // LIGHTBOX STATES
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        console.log("Fetching news with ID:", id);
        const res = await newsAPI.getById(id);
        console.log("API Response:", res);
        console.log("Response data:", res.data);

        const data = res.data.data;
        console.log("News data:", data);

        setNews(data);

        const images = [];
        if (data.main_image_url) {
          images.push(data.main_image_url);
        }
        if (data.additional_images?.length > 0) {
          data.additional_images.forEach((img) => {
            images.push(img.image_url);
          });
        }
        console.log("Images:", images);
        setAllImages(images);

        // Fetch related news
        const related = await newsAPI.getAll();
        console.log("Related news response:", related);

        const filtered = related.data.data
          .filter((item) => {
            const newsId = item.news_id;
            return String(newsId) !== String(id);
          })
          .slice(0, 5);

        console.log("Filtered related:", filtered);
        setRelatedNews(filtered);
      } catch (err) {
        console.error("Error fetching news:", err);
        if (err.response) {
          console.error("Error response:", err.response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // KEYBOARD CONTROL
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;

      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") setLightboxIndex(null);
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, allImages]);

  const nextImage = () => {
    setLightboxIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4"></i>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            ไม่พบข้อมูล
          </h2>
          <Link to="/news" className="text-primary-500 hover:underline">
            กลับไปหน้าข่าวประชาสัมพันธ์
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {news?.title
            ? `${news.title} | ข่าวประชาสัมพันธ์`
            : "ข่าวประชาสัมพันธ์"}
        </title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container-custom">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-8 flex-wrap">
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              หน้าแรก
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <Link
              to="/news"
              className="text-primary-500 hover:text-primary-600 transition-colors"
            >
              ข่าวประชาสัมพันธ์
            </Link>
            <i className="fas fa-chevron-right text-xs"></i>
            <span className="text-gray-800 font-medium line-clamp-1">
              {news.title}
            </span>
          </div>

          {/* Main Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Header */}
                <div className="p-8 border-b">
                  <h1 className="text-3xl lg:text-4xl font-bold text-primary-500 mb-4">
                    {news.title}
                  </h1>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <i className="fas fa-calendar-alt text-primary-500"></i>
                      {formatDate(news.publish_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="fas fa-user text-primary-500"></i>
                      {news.author || "ไม่ระบุ"}
                    </span>
                  </div>
                </div>

                {/* Featured Image */}
                {allImages.length > 0 && (
                  <div
                    className="relative cursor-pointer group"
                    onClick={() => setLightboxIndex(0)}
                  >
                    <img
                      src={allImages[0]}
                      alt={news.title}
                      className="w-full h-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/images/no-image.png";
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                      <i className="fas fa-search-plus text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-8">
                  <div
                    className="prose max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: news.content }}
                  />
                </div>

                {/* Gallery */}
                {allImages.length > 1 && (
                  <div className="p-8 border-t">
                    <h3 className="text-xl font-semibold text-primary-500 mb-4 flex items-center">
                      <i className="fas fa-images text-secondary-500 mr-2"></i>
                      ภาพกิจกรรม
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {allImages.slice(1).map((img, i) => (
                        <div
                          key={i}
                          className="relative cursor-pointer group aspect-square"
                          onClick={() => setLightboxIndex(i + 1)}
                        >
                          <img
                            src={img}
                            alt={`${news.title} ${i + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/assets/images/no-image.png";
                            }}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                            <i className="fas fa-search-plus text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back Button */}
                <div className="p-8 bg-gray-50 border-t">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    แชร์องค์ความรู้นี้
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        alert("คัดลอกลิงก์เรียบร้อย");
                      }}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                    >
                      <i className="fas fa-link"></i>
                    </button>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#1877f2] text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                    >
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a
                      href={`https://line.me/R/msg/text/?${news.title}%20${window.location.href}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#00c300] text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                    >
                      <i className="fab fa-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-primary-500 mb-4 pb-2 border-b flex items-center">
                  <i className="fas fa-list text-secondary-500 mr-2"></i>
                  ข่าวอื่นๆ
                </h3>

                {relatedNews.length > 0 ? (
                  <div className="space-y-4">
                    {relatedNews.map((item) => {
                      const newsId = item?._id || item?.id || item?.news_id;
                      if (!newsId) return null;

                      return (
                        <Link
                          key={newsId}
                          to={`/news/${newsId}`}
                          className="flex gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={
                                item.main_image_url ||
                                "/assets/images/no-image.png"
                              }
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/assets/images/no-image.png";
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                              {item.title}
                            </h4>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    ไม่มีข่าวอื่นๆ
                  </p>
                )}
                {/* Back to Knowledge */}
                <div className="mt-6 pt-4 border-t">
                  <Link
                    to="/news"
                    className="flex items-center justify-center gap-2 text-primary-500 hover:text-primary-600 transition-colors"
                  >
                    <i className="fas fa-arrow-left"></i>
                    <span>กลับไปหน้าข่าวประชาสัมพันธ์</span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* LIGHTBOX */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-10"
          >
            ×
          </button>

          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 transition-colors"
              >
                ‹
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 transition-colors"
              >
                ›
              </button>
            </>
          )}

          <img
            src={allImages[lightboxIndex]}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
              {lightboxIndex + 1} / {allImages.length}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default NewsDetailPage;
